/** API tokens: list, create (the token and login link are shown ONCE), revoke. */
import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useAction } from "../lib/actions";
import { fmtWhen } from "../lib/time";
import { ActionError, ConfirmButton, CopyButton, QueryView, Section, TableScroll } from "../lib/ui";

type TokenKind = Schemas["TokenKind"];
type Scope = Schemas["Scope"];
const KINDS: TokenKind[] = ["agent", "human", "worker"];
const SCOPES: Scope[] = ["read", "make", "review", "schedule", "admin", "worker"];
export const DEFAULT_SCOPES: Record<TokenKind, Scope[]> = {
  human: ["read", "make", "review", "schedule", "admin"],
  agent: ["read", "make", "review", "schedule"],
  worker: ["read", "worker"],
};

function CreatedToken({ t, onDone }: { t: Schemas["TokenCreated"]; onDone: () => void }) {
  return (
    <div className="ws7-secret" role="region" aria-label="New token (shown once)" data-testid="token-created">
      <p><strong>Token #{t.id} for {t.actor}</strong> — shown once. Copy it now; it cannot be shown again.</p>
      <p>Token: <code data-testid="token-value">{t.token}</code></p>
      <p>Login link: <code data-testid="token-login-url">{t.login_url}</code></p>
      <div className="ws7-row">
        <CopyButton text={t.token} label="Copy token" testId="token-copy" what={`token #${t.id}`} />
        <CopyButton text={t.login_url} label="Copy login link" testId="token-copy-link" what={`login link of token #${t.id}`} />
        <button type="button" data-testid="token-done" onClick={onDone}>Done (hide token)</button>
      </div>
    </div>
  );
}

export function TokensTab() {
  const tokens = useQuery({ queryKey: qk.list("tokens"), queryFn: () => unwrap(api.GET("/api/tokens")) });
  const [actor, setActor] = useState("");
  const [kind, setKind] = useState<TokenKind>("agent");
  const [scopes, setScopes] = useState<Scope[] | null>(null);
  const [note, setNote] = useState("");
  const [created, setCreated] = useState<Schemas["TokenCreated"] | null>(null);
  const effectiveScopes = scopes ?? DEFAULT_SCOPES[kind];

  const create = useAction(
    (body: Schemas["TokenIn"]) => unwrap(api.POST("/api/tokens", { body })),
    {
      label: "Create token",
      success: (t) => `Created token #${t.id} for ${t.actor} (${t.kind}); copy it now, it is shown once`,
      invalidate: [qk.list("tokens")],
    },
  );
  const revoke = useAction(
    (t: Schemas["TokenOut"]) => unwrap(api.DELETE("/api/tokens/{token_id}", { params: { path: { token_id: t.id } } })),
    { label: (t) => `Revoke token #${t.id}`, success: (_r, t) => `Revoked token #${t.id} (${t.actor})`, invalidate: [qk.list("tokens")] },
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const t = await create.run({ actor: actor.trim(), kind, scopes: scopes ?? undefined, note: note.trim() || undefined });
    if (t) {
      setCreated(t);
      setActor("");
      setNote("");
      setScopes(null);
    }
  };

  return (
    <div className="ws7-page">
      <Section id="token-create" title="Create a token">
        {created && <CreatedToken t={created} onDone={() => setCreated(null)} />}
        <form className="ws7-form" aria-label="Create token" onSubmit={submit}>
          <div className="ws7-toolbar">
            <label className="ws7-field">
              Actor
              <input value={actor} required maxLength={64} data-testid="token-actor" onChange={(e) => setActor(e.target.value)} />
            </label>
            <label className="ws7-field">
              Kind
              <select value={kind} data-testid="token-kind" onChange={(e) => { setKind(e.target.value as TokenKind); setScopes(null); }}>
                {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </label>
            <label className="ws7-field">
              Note
              <input value={note} maxLength={300} data-testid="token-note" onChange={(e) => setNote(e.target.value)} />
            </label>
          </div>
          <fieldset className="ws7-setting-group">
            <legend>Scopes (default for {kind}: {DEFAULT_SCOPES[kind].join(", ")})</legend>
            <div className="ws7-row">
              {SCOPES.map((s) => (
                <label key={s} className="ws7-check">
                  <input type="checkbox" checked={effectiveScopes.includes(s)} data-testid={`token-scope-${s}`}
                    onChange={(e) => setScopes(e.target.checked ? [...effectiveScopes, s] : effectiveScopes.filter((x) => x !== s))} />
                  {s}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="ws7-row">
            <button type="submit" className="ws7-primary" data-testid="token-create" disabled={create.pending || !actor.trim()}>
              Create token
            </button>
          </div>
          <ActionError error={create.error} />
        </form>
      </Section>
      <Section id="token-list" title="Tokens">
        <ActionError error={revoke.error} onReload={() => void tokens.refetch()} />
        <QueryView q={tokens} what="tokens">
          {(rows) => (
            <TableScroll label="Token table">
              <table className="ws7-table" aria-label="Tokens" data-testid="tokens-table">
                <thead>
                  <tr>
                    <th scope="col" className="num">#</th>
                    <th scope="col">Actor</th>
                    <th scope="col">Kind</th>
                    <th scope="col">Scopes</th>
                    <th scope="col">Note</th>
                    <th scope="col">Created</th>
                    <th scope="col">Last used</th>
                    <th scope="col">State</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id} data-testid={`token-row-${t.id}`}>
                      <td className="num">{t.id}</td>
                      <td>{t.actor}</td>
                      <td>{t.kind}</td>
                      <td>{t.scopes.join(", ")}</td>
                      <td>{t.note ?? ""}</td>
                      <td>{fmtWhen(t.created_at)}</td>
                      <td>{fmtWhen(t.last_used_at) || "never"}</td>
                      <td data-testid={`token-state-${t.id}`}>{t.revoked_at ? `revoked ${fmtWhen(t.revoked_at)}` : "active"}</td>
                      <td>
                        {!t.revoked_at && (
                          <ConfirmButton label={`Revoke token #${t.id} (${t.actor})`} confirmLabel={`Confirm revoke token #${t.id}`}
                            testId={`token-revoke-${t.id}`} disabled={revoke.pending} onConfirm={() => void revoke.run(t)} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </QueryView>
      </Section>
    </div>
  );
}
